import React from 'react';
import { Button, Select, Input, Upload as AntUpload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { FileText, CheckCircle } from 'lucide-react';
import { StyledModal } from '../constants/styles';
import { DOCUMENT_TYPES } from '../constants/documentTypes';

const UploadModal = ({
  isUploadModalVisible,
  handleUploadModalOk,
  handleUploadModalCancel,
  file,
  handleFileChange,
  uploadCompany,
  setUploadCompany,
  uploadType,
  setUploadType,
  isFileUploaded,
  companies,
}) => {
  return (
    <StyledModal
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <UploadOutlined className="text-blue-500 text-lg" />
          </div>
          <span className="font-semibold text-gray-800">Upload New Document</span>
        </div>
      }
      open={isUploadModalVisible}
      onOk={handleUploadModalOk}
      onCancel={handleUploadModalCancel}
      okText="Upload Document"
      okButtonProps={{
        disabled: !isFileUploaded || !uploadCompany || !uploadType,
        style: {
          background: !isFileUploaded || !uploadCompany || !uploadType 
            ? '#e2e8f0' 
            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: '8px',
          border: 'none',
        }
      }}
      cancelButtonProps={{
        style: {
          borderRadius: '8px',
        }
      }}
      width={500}
    >
      <div className="space-y-4 mt-4">
        {/* File Upload Section */}
        <div className="border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
          <AntUpload
            beforeUpload={() => false}
            onChange={handleFileChange}
            maxCount={1}
            fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
            onRemove={() => {
              handleFileChange({ fileList: [] });
            }}
          >
            <Button 
              icon={<UploadOutlined />}
              className="w-full h-20 flex items-center justify-center border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:text-blue-500 transition-all"
            >
              <span className="ml-2">Click or drag file to upload</span>
            </Button>
          </AntUpload>
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <FileText size={12} />
            <span>Supported: PDF, Word, Excel, PowerPoint</span>
          </div>
        </div>

        {/* Company Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
          <Select
            placeholder="Select company"
            value={uploadCompany}
            onChange={setUploadCompany}
            className="w-full"
            showSearch
            allowClear
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                  <Input
                    placeholder="Enter custom company name"
                    value={uploadCompany}
                    onChange={(e) => {
                      e.stopPropagation();
                      setUploadCompany(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
            )}
          >
            {companies.map(company => (
              <Select.Option key={company} value={company}>
                <span style={{
                  fontWeight: 500,
                  color: '#374151'
                }}>
                  {company}
                </span>
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Document Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
          <Select
            placeholder="Select document type"
            value={uploadType}
            onChange={setUploadType}
            className="w-full"
            showSearch
            allowClear
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                  <Input
                    placeholder="Enter custom document type"
                    value={uploadType}
                    onChange={(e) => {
                      e.stopPropagation();
                      setUploadType(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
            )}
          >
            {DOCUMENT_TYPES.map(type => (
              <Select.Option key={type} value={type}>
                <span style={{
                  fontWeight: 500,
                }}>
                  {type}
                </span>
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Success Message */}
        {isFileUploaded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
          >
            <div className="p-1 bg-green-500 rounded-full">
              <CheckCircle size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800">File ready to upload</p>
              <p className="text-xs text-green-600">{file?.name}</p>
            </div>
          </motion.div>
        )}
      </div>
    </StyledModal>
  );
};

export default UploadModal;